import React, { useState } from 'react';
import { joinClasses } from '../../../../Utils/className.util';
import { FormattedDateTime } from '../../../../Components/FormattedDate';
import Text from '../../../../Components/Base/Text/Text';
import { Message } from '../../../../graphql/graphql';
import styles from './NotificationsSidebarItem.module.scss';
import apiClient from '../../../../Services/ApiClient';
import NetteApiClient from '../../../../Services/NetteApiClient';
import { NOTIFICATION_TYPE } from '../../Enums/notification.enum';
import useNotificationTemplate from '../../Hooks/useNotificationTemplate';
import { useAppContext } from '../../../../Services/AppContext';
import NotificationDocumentationFileModal from '../NotificationDocumentationFile/NotificationDocumentationFileModal';
import { useDisclose } from '../../../Modal/Hooks/useDisclose';
import NewInAppModal from '../NewInApp/NewInAppModal';

interface Props {
	item: Message;
	onUpdate?: () => void;
}

const NotificationsSidebarItem: React.FC<Props> = props => {
	const { item, onUpdate } = props;
	const { formattedMessage } = useNotificationTemplate(item as Message);
	const { userData } = useAppContext();
	const [isFileOpen, setIsFileOpen] = useState(false);
	const {
		isOpen: isNewOpen,
		onClose: onCloseNew,
		onOpen: onOpenNew,
	} = useDisclose(false);

	const handleOpenNotification = async () => {
		if (
			item.type === NOTIFICATION_TYPE.DOCUMENTATION_FILE &&
			!item.content?.fileId &&
			item.content?.url
		) {
			window.open(item.content.url, '_blank');
		}

		await apiClient.get('/notifications/' + item.id);
		onUpdate?.();

		const patientId = item.content.patientId;

		switch (item.type) {
			case NOTIFICATION_TYPE.CASE:
				NetteApiClient.get('/ordinace/list');
				break;
			case NOTIFICATION_TYPE.LINK:
				// @ts-ignore
				window.location.href = item.content?.url;
				break;
			case NOTIFICATION_TYPE.MESSAGE:
				NetteApiClient.get(
					'/centrum-akci/detail-zpravy-react/' + item.content?.conversationId,
				);
				break;
			case NOTIFICATION_TYPE.DOCUMENTATION_FILE:
				if (item.content.fileId) {
					setIsFileOpen(true);
				}
				break;
			case NOTIFICATION_TYPE.EVENTS:
				const eventId = item.messageAttachments?.find(
					item => item?.eventId,
				)?.eventId;
				if (eventId) {
					NetteApiClient.get('/calendar/?eventId=' + eventId);
				}
				break;
			case NOTIFICATION_TYPE.PATIENT_PROFILE:
				if (patientId) {
					NetteApiClient.get(`/profil-pacienta/${patientId}`);
				}
				break;
			case NOTIFICATION_TYPE.PATIENT_PROFILE_UPDATE:
				if (patientId) {
					NetteApiClient.get(
						`/profil-pacienta/${patientId}?handle=dataCompare`,
					);
				}
				break;
			case NOTIFICATION_TYPE.NEW_IN_MEDDI_MD:
				onOpenNew();
				break;
			case NOTIFICATION_TYPE.LABORATORY_RESULT:
				const laboratoryResultId: string = item.content.laboratoryResultId;
				if (laboratoryResultId) {
					NetteApiClient.get(`/laboratory-result/${laboratoryResultId}`);
				}
				break;
			default:
				NetteApiClient.get('/oznameni/' + item.id);
		}
	};

	const showSender: boolean = !(item.sender?.user?.id === userData.id);

	return (
		<>
			<div
				className={joinClasses([
					styles.wrapper,
					item.myMessageMember?.seen ? '' : styles.unseen,
				])}
				onClick={handleOpenNotification}>
				<div className={styles.date}>
					<FormattedDateTime datetime={item.createdAt} />
				</div>
				{item.content?.imageUrl && (
					<div
						className={styles.image}
						style={{
							backgroundImage: 'url("' + item.content?.imageUrl + '")',
						}}>
						&nbsp;
					</div>
				)}
				{item.sender?.user?.profile?.fullName && showSender && (
					<div className={styles.title}>
						<Text tx={'base.shared.from'} firstUpper inline />:{' '}
						{item.sender.user?.profile?.fullName || ''}
					</div>
				)}
				<div className={styles.title}>{item.content?.title || ''}</div>
				<div className={styles.text}>
					<Text tx={formattedMessage() || ''} truncate={60} />
				</div>
			</div>
			{isFileOpen && item.content?.fileId && (
				<NotificationDocumentationFileModal
					documentationFileId={item.content?.fileId}
					onClose={() => setIsFileOpen(false)}
				/>
			)}
			{isNewOpen && (
				<NewInAppModal data={item.content.data} isOpen onClose={onCloseNew} />
			)}
		</>
	);
};

export default NotificationsSidebarItem;
